import { useEffect, useState } from 'react';
import { Wordmark } from '@/components/Brand';
import { Button } from '@/components/Button';
import { Alert } from '@/components/Alert';
import { Input } from '@/components/Field';

// Login is intentionally minimal: link issuance is closed (admin-only via
// CLI). The page exists so an unauthenticated visitor lands somewhere
// helpful instead of a blank app shell. If a magic link is in the URL, we
// surrender to the browser — the server's GET /api/auth/verify already
// sets the cookie and 303s back to /.
//
// The one extra affordance is the login-code form: an already-signed-in
// user can mint a one-time four-word code in Settings on another device
// and type it here (POST /api/auth/code sets the session cookie).
export function Login() {
  useEffect(() => {
    if (window.location.pathname === '/api/auth/verify') {
      // Hosting under a SPA fallback shouldn't normally happen since /api
      // is reverse-proxied, but if it did, send the request through.
      window.location.assign(window.location.href);
    }
  }, []);

  return (
    <div className="mx-auto max-w-md p-8">
      <Wordmark glyphSize={40} className="mb-3 text-3xl" />
      <p className="mb-4 text-slate-600">
        This site is invite-only. Ask Andrew to send you a sign-in link by SMS.
      </p>
      <p className="mb-6 text-xs text-slate-500">
        Once you tap the link, your browser will land back on the home page
        signed in.
      </p>
      <CodeForm />
    </div>
  );
}

function CodeForm() {
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (busy || !code.trim()) return;
    setBusy(true);
    setError(null);
    try {
      const r = await fetch('/api/auth/code', {
        method: 'POST',
        credentials: 'include',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ code }),
      });
      if (r.status === 401) {
        throw new Error('That code is invalid or has expired — codes work once and last 15 minutes.');
      }
      if (!r.ok) {
        throw new Error(`Sign-in failed (${r.status}).`);
      }
      // Cookie is set; reload the shell so it re-fetches /api/me signed in.
      window.location.assign('/');
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
      setBusy(false);
    }
  }

  return (
    <form onSubmit={(e) => void submit(e)} className="space-y-2 border-t border-slate-200 pt-4">
      <label htmlFor="login-code" className="block text-sm font-medium text-slate-700">
        Have a login code?
      </label>
      <p className="text-xs text-slate-500">
        Already signed in on another device? Generate a one-time code under Settings there
        and type its four words here.
      </p>
      <div className="flex items-center gap-2">
        <Input
          id="login-code"
          type="text"
          value={code}
          onChange={(e) => setCode(e.target.value)}
          placeholder="correct horse battery staple"
          autoComplete="off"
          autoCapitalize="none"
          spellCheck={false}
          className="min-w-0 flex-1 font-mono"
        />
        <Button type="submit" variant="primary" className="shrink-0" loading={busy} disabled={!code.trim()}>
          {busy ? 'Signing in…' : 'Sign in'}
        </Button>
      </div>
      {error && <Alert>{error}</Alert>}
    </form>
  );
}
